import axios from 'axios'
import type { Pinia } from 'pinia'
import { useUserStore } from '../stores/user'
import router from '../router'
import { useToast } from 'vuestic-ui'
import { AuthApi } from './auth'
import { globalLoading } from './loading-state'

export const http = axios.create({
  baseURL: import.meta.env.VITE_API_BASE || 'https://localhost:7278',
  withCredentials: true,
})

const { init } = useToast()

let isLoggingOut = false
let reissuing: Promise<boolean> | null = null

async function tryReissueOnce() {
  if (!reissuing) {
    reissuing = AuthApi.reissue()
      .then(() => true)
      .catch(() => false)
      .finally(() => {
        reissuing = null
      })
  }
  return reissuing
}

export function setupHttp(pinia: Pinia) {
  const user = useUserStore(pinia)

  http.interceptors.request.use(
    (cfg) => {
      globalLoading.count++
      return cfg
    },
    (error) => {
      if (globalLoading.count > 0) globalLoading.count--
      return Promise.reject(error)
    },
  )

  async function forceLogout() {
    await AuthApi.logout()
    user.clear()
    init({ message: 'Hesabınız pasif duruma alınmış. Lütfen yöneticiniz ile iletişime geçiniz.', color: 'danger' })
    await router.push({ name: 'login' })
  }

  http.interceptors.response.use(
    (r) => {
      if (globalLoading.count > 0) globalLoading.count--
      return r
    },
    async (error) => {
      if (globalLoading.count > 0) globalLoading.count--

      const { response, config } = error || {}
      const url = (error?.config?.url || '').toLowerCase()
      const isAuthCall = url.includes('/api/auth')

      if (response && response.status === 512) {
        await forceLogout()
        return Promise.reject(error)
      }

      if (response && response.status === 401 && !isAuthCall) {
        if (!config.__triedReissue) {
          config.__triedReissue = true
          const ok = await tryReissueOnce()
          if (ok) {
            return http.request(config)
          }
        }

        if (!isLoggingOut) {
          isLoggingOut = true
          try {
            user.clear()
            const redirect = router.currentRoute.value.fullPath
            await router.push({ name: 'login', query: { redirect } })
          } finally {
            setTimeout(() => {
              isLoggingOut = false
            }, 300)
          }
          init({ message: 'Oturumunuzun süresi doldu. Lütfen tekrar giriş yapın.', color: 'danger' })
        }
      } else if (response && response.status === 409) {
        init({ message: 'Bu slug zaten kullanımda. Lütfen farklı bir slug girin.', color: 'warning' })
      } else if (response && response.status === 403) {
        init({
          message: 'Bu işlem için yetkiniz yok. Eğer yetki verildiyse lütfen çıkış yapıp tekrardan giriş yapınız.',
          color: 'warning',
        })
      }
      return Promise.reject(error)
    },
  )
}
